import { useState } from 'react';
import { KeyRound, X } from 'lucide-react';
import { api, ApiError } from '../api/client';

// Changement de mot de passe par l'utilisateur.
//
// Il n'existait aucun moyen d'en changer : clients et fournisseurs conservaient
// indéfiniment le secret provisoire généré à la création de leur compte, connu de
// l'administration et transmis par courriel.

interface ChangePasswordDialogProps {
  onClose: () => void;
  brandColor?: string;
}

const MIN_LENGTH = 10;

export default function ChangePasswordDialog({
  onClose,
  brandColor = '#D95D39',
}: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_LENGTH) {
      setError(`Le nouveau mot de passe doit contenir au moins ${MIN_LENGTH} caractères.`);
      return;
    }
    if (newPassword !== confirmation) {
      setError('Les deux saisies ne correspondent pas.');
      return;
    }

    setPending(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setDone(true);
      setTimeout(onClose, 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Changement impossible.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[400] flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-black uppercase tracking-wide text-slate-900 flex items-center gap-2">
            <KeyRound className="w-4 h-4" style={{ color: brandColor }} />
            Changer mon mot de passe
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <p className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3 py-2.5 text-xs font-bold leading-relaxed">
            Mot de passe mis à jour. Vos autres appareils connectés ont été déconnectés.
          </p>
        ) : (
          <>
            <div className="space-y-1">
              <label htmlFor="cp-current" className="block text-[10px] font-black uppercase tracking-wider text-slate-500">
                Mot de passe actuel
              </label>
              <input
                id="cp-current"
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="cp-new" className="block text-[10px] font-black uppercase tracking-wider text-slate-500">
                Nouveau mot de passe
              </label>
              <input
                id="cp-new"
                type="password"
                required
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
              <p className="text-[10px] text-slate-500 pt-0.5">{MIN_LENGTH} caractères minimum.</p>
            </div>

            <div className="space-y-1">
              <label htmlFor="cp-confirm" className="block text-[10px] font-black uppercase tracking-wider text-slate-500">
                Confirmation
              </label>
              <input
                id="cp-confirm"
                type="password"
                required
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            {error && (
              <p role="alert" className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={pending}
              className="w-full py-2.5 rounded-xl text-white text-xs font-black uppercase tracking-wider cursor-pointer disabled:opacity-60 transition-opacity"
              style={{ backgroundColor: brandColor }}
            >
              {pending ? 'Enregistrement…' : 'Changer le mot de passe'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
